import { useCallback, useEffect, useRef } from 'react';
import useGameStore from '@store/useGameStore';
import useCinematicStore from '@store/useCinematicStore';
import { EMPTY_ARRAY } from '@utils/constants';
import EventCard from './EventCard';
import './CinematicPanel.css';

const CinematicPanel = () => {
  
  // --- Subscribe to cinematic store ---
  const currentActionCardIndex = useCinematicStore((state) => state.currentActionCardIndex);
  const executedUpToIndex = useCinematicStore((state) => state.executedResolutionQueueIndex);
  const isAnimating = useCinematicStore((state) => state.isAnimating);
  
  // Track what props changed to cause re-render
  const prevCinematicState = useRef({ 
    currentActionCardIndex, 
    executedUpToIndex, 
    isAnimating, 
    isAutoPlay: false, 
    playbackSpeed: 1 
  });
  
  // These don't exist yet but we'll add them later
  const isAutoPlay = useCinematicStore((state) => state.isAutoPlay) || false;
  const playbackSpeed = useCinematicStore((state) => state.playbackSpeed) || 1;
  
  // Track re-renders and what caused them
  useEffect(() => {
    /*const changes = [];
    if (prevCinematicState.current.currentActionCardIndex !== currentActionCardIndex) changes.push('currentActionCardIndex');
    if (prevCinematicState.current.executedUpToIndex !== executedUpToIndex) changes.push('executedUpToIndex');
    if (prevCinematicState.current.isAnimating !== isAnimating) changes.push('isAnimating');
    if (prevCinematicState.current.isAutoPlay !== isAutoPlay) changes.push('isAutoPlay');
    if (prevCinematicState.current.playbackSpeed !== playbackSpeed) changes.push('playbackSpeed');
    
    if (changes.length > 0) {
      console.log(`[CinematicPanel] Re-rendered because these cinematic props changed:`, changes, {
        currentActionCardIndex: `${prevCinematicState.current.currentActionCardIndex} → ${currentActionCardIndex}`,
        executedUpToIndex: `${prevCinematicState.current.executedUpToIndex} → ${executedUpToIndex}`,
        isAnimating: `${prevCinematicState.current.isAnimating} → ${isAnimating}`,
        isAutoPlay: `${prevCinematicState.current.isAutoPlay} → ${isAutoPlay}`,
        playbackSpeed: `${prevCinematicState.current.playbackSpeed} → ${playbackSpeed}`
      });
    }*/
    
    prevCinematicState.current = { currentActionCardIndex, executedUpToIndex, isAnimating, isAutoPlay, playbackSpeed };
  });
  
  // Store actions
  const selectCard = useCinematicStore((state) => state.selectCard);
  const executeCard = useCinematicStore((state) => state.executeCard);
  const setAutoPlay = useCinematicStore((state) => state.setAutoPlay);
  const setPlaybackSpeed = useCinematicStore((state) => state.setPlaybackSpeed);
  const nextCard = useCinematicStore((state) => state.nextCard);
  const previousCard = useCinematicStore((state) => state.previousCard);
  
  // --- Get game state ---
  // TEST: Comment out subscription to prevent re-renders
  // const isResolutionPhase = useGameStore((state) => state.isResolutionPhase);
  const isResolutionPhase = useGameStore.getState().isResolutionPhase;
  
  // --- Get resolution queue with proper validation ---
  let resolutionQueue = useGameStore.getState().getResolutionQueue() || EMPTY_ARRAY;
  if (!resolutionQueue) {
    console.warn('[CinematicPanel] No resolution queue available - tactical replay data missing!');
  }
    
  if (!isResolutionPhase) {
    console.warn('[CinematicPanel] We are not on cinematic mode!');
  }
  
  
  // --- Click handler for action cards ---
  const handleActionCardClick = useCallback((clickedIndex) => {
    const state = useCinematicStore.getState();
    
    if (clickedIndex === state.currentActionCardIndex && clickedIndex > state.executedResolutionQueueIndex) {
      executeCard();
    } else {
      selectCard(clickedIndex);
    }
  }, [selectCard, executeCard]);
  
  // --- Click handler for final "current" card ---
  const handleFinalCardClick = useCallback(() => {
    // The "current" card represents the final state after all actions
    // We want to select it visually without executing anything
    const endCardIndex = resolutionQueue.length; // This is the index for the end card
    
    // Just select the end card visually - this will show the final state
    selectCard(endCardIndex);
  }, [resolutionQueue.length, selectCard]);
  
  // --- Play/Pause button handler ---
  const handlePlayPause = useCallback(() => {
    if (isAutoPlay) {
      // Stop auto-play
      // console.log('[CinematicPanel] Stopping auto-play');
      setAutoPlay(false);
    } else {
      // Start auto-play
      // console.log('[CinematicPanel] Starting auto-play');
      setAutoPlay(true);
      
      // If we're not animating, start the animation
      if (!isAnimating) {
        executeCard();
      }
    }
  }, [isAutoPlay, isAnimating, setAutoPlay, executeCard]);
  
  // --- Keyboard shortcuts ---
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Don't handle if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }
      
      switch(e.key) {
        case ' ':
          e.preventDefault();
          handlePlayPause();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          previousCard();
          break;
        case 'ArrowRight':
          e.preventDefault();
          nextCard();
          break;
        case 'Enter':
          e.preventDefault();
          if (!isAnimating) {
            executeCard();
          }
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handlePlayPause, previousCard, nextCard, executeCard, isAnimating]);
  
  if (!isResolutionPhase) {
    return null;
  }

  // --- Render ---
  return (
    <div className="cinematic-panel game-panel">
      
      <div className="cinematic-controls">
        <div className="controls-container game-panel">
          <span className="speed-text">{playbackSpeed}x</span>
          
          <input
            type="range"
            min="0.25"
            max="3"
            step="0.25"
            value={playbackSpeed}
            onChange={(e) => setPlaybackSpeed(parseFloat(e.target.value))}
            className="speed-slider"
            aria-label="Playback speed"
          />
          
          <button 
            className="play-btn btn-enhanced"
            onClick={handlePlayPause}
            aria-label={isAutoPlay ? "Pause cinematic" : "Play cinematic"}
          >
            {isAutoPlay ? (
              // Pause icon
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
              </svg>
            ) : (
              // Play icon
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z"/>
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Action Cards Display - Horizontal Row */}
      <div className="action-timeline-container">
        
        <div className="action-timeline-row">
          {/* Resolution Queue Events - 0-indexed */}
          {resolutionQueue.map((event, index) => {
            // Determine card state
            const isSelected = index === currentActionCardIndex;
            const isExecuted = index <= executedUpToIndex;
            const isCurrentlyAnimating = index === currentActionCardIndex && isAnimating;
            
            return (
              <EventCard
                key={event.id || `event-${index}`}
                event={event}
                index={index}
                isSelected={isSelected}
                isExecuted={isExecuted}
                isAnimating={isCurrentlyAnimating}
                showPlayButton={isSelected && !isAnimating && index > executedUpToIndex}
                onClick={() => handleActionCardClick(index)}
              />
            );
          })}
          
          {/* End State Card - represents final state */}
          <EventCard
            key="end-state"
            event={{
              id: 'artificial-end',
              isArtificial: true,
              type: 'current_state',
              action: { name: 'Final State' }
            }}
            index="current"
            isSelected={currentActionCardIndex === resolutionQueue.length}
            isExecuted={executedUpToIndex === resolutionQueue.length - 1}
            isAnimating={false}
            showPlayButton={false}
            onClick={handleFinalCardClick}
          />
        </div>
      </div>
    </div>
  );
};

export default CinematicPanel;